import { useEffect, useRef, useState } from 'react';
import { attachmentApi, type VoucherAttachment } from '../api/client.js';
import { EmptyState, ErrorBanner } from './States.js';
import { roleCan, permHint } from '../utils/permissions.js';

interface Props {
  entryId: string;
  reference: string;
  onClose: () => void;
}

function formatSize(n: number): string {
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

const ACCEPT = '.png,.jpg,.jpeg,.webp,.gif,.pdf';

/**
 * 凭证影像弹窗：缩略图网格（blob+objectURL 预览，携带 token）+ 上传 + 删除。
 * 单文件 ≤5MB，图片（png/jpg/webp/gif）与 PDF。
 */
export default function AttachmentModal({ entryId, reference, onClose }: Props) {
  const canManage = roleCan('journal:create');
  const [atts, setAtts] = useState<VoucherAttachment[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const urlsRef = useRef<Record<string, string>>({});

  const revokeAll = (map: Record<string, string>) => {
    Object.values(map).forEach((u) => URL.revokeObjectURL(u));
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const list = await attachmentApi.list(entryId);
      setAtts(list);
      // 拉取预览 blob（带鉴权头，<img src> 直连拿不到 token）
      const next: Record<string, string> = {};
      await Promise.all(
        list.map(async (a) => {
          try {
            const blob = await attachmentApi.file(a.id);
            next[a.id] = URL.createObjectURL(blob);
          } catch (e: any) {
            console.warn(`附件预览失败 ${a.fileName}:`, e?.message ?? e); // 单张预览失败不阻断列表
          }
        })
      );
      revokeAll(urlsRef.current);
      urlsRef.current = next;
      setUrls(next);
    } catch (e: any) {
      setError(`加载附件失败：${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => revokeAll(urlsRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId]);

  const doUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError('');
    for (const f of Array.from(files)) {
      try {
        await attachmentApi.upload(entryId, f);
      } catch (e: any) {
        setError(`${f.name} 上传失败：${e.message}`);
        break; // 超限/类型错误后续大概率同样失败，早停并保留已传
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
    await load();
  };

  const doRemove = async (a: VoucherAttachment) => {
    if (!window.confirm(`删除附件「${a.fileName}」？影像将一并移除。`)) return;
    try {
      await attachmentApi.remove(a.id);
      await load();
    } catch (e: any) {
      setError(`删除失败：${e.message}`);
    }
  };

  const openOriginal = async (a: VoucherAttachment) => {
    try {
      const blob = await attachmentApi.file(a.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      setError(`打开原图失败：${e.message}`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-glow w-full max-w-2xl max-h-[85vh] flex flex-col animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-slate-800">📎 凭证影像</h3>
            <p className="text-xs text-slate-500">
              {reference} · 图片/PDF，单个 ≤5MB，最多 20 张
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept={ACCEPT} multiple hidden onChange={(e) => doUpload(e.target.files)} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading || !canManage}
              title={permHint('journal:create')}
              className="btn btn-primary btn-sm"
            >
              {uploading ? '上传中…' : '+ 上传影像'}
            </button>
            <button onClick={onClose} className="btn btn-ghost btn-sm">
              关闭
            </button>
          </div>
        </div>

        {/* 主体 */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-4">
              <ErrorBanner message={error} onRetry={loading ? undefined : load} />
            </div>
          )}

          {loading ? (
            <div className="py-10 text-center text-sm text-slate-400 animate-pulse">加载影像中…</div>
          ) : atts.length === 0 ? (
            <EmptyState
              icon="🖼️"
              title="还没有原始凭证影像"
              hint="上传发票、回单、收据的照片或 PDF，审批与查账时可直接调阅"
              action={
                canManage ? (
                  <button onClick={() => fileRef.current?.click()} className="btn btn-primary btn-sm">
                    + 上传第一张影像
                  </button>
                ) : undefined
              }
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {atts.map((a) => (
                <div key={a.id} className="group border border-slate-200 rounded-xl overflow-hidden bg-white hover:shadow-md transition-shadow">
                  <div className="h-28 bg-slate-50 grid place-items-center overflow-hidden">
                    {a.mimeType === 'application/pdf' ? (
                      <span className="text-3xl" title="PDF 文档">📄</span>
                    ) : urls[a.id] ? (
                      <img src={urls[a.id]} alt={a.fileName} className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-xs text-slate-400">预览失败</span>
                    )}
                  </div>
                  <div className="px-2.5 py-2">
                    <p className="text-xs font-medium text-slate-700 truncate" title={a.fileName}>{a.fileName}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[11px] text-slate-400">{formatSize(a.sizeBytes)}</span>
                      <span className="flex items-center gap-2">
                        <button onClick={() => openOriginal(a)} className="text-[11px] text-brand-600 hover:underline">
                          查看原图
                        </button>
                        <button
                          onClick={() => doRemove(a)}
                          disabled={!canManage}
                          title={permHint('journal:create')}
                          className="text-[11px] text-red-500 hover:underline disabled:text-slate-300 disabled:no-underline disabled:cursor-not-allowed"
                        >
                          删除
                        </button>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
